// CarouselThumbnail — renders a 2×2 collage of the 4 frames inside a
// carousel post's zip blob. Carousels in this app are stored as a
// single .zip in the gallery bucket (per the save flow in StepText.tsx),
// so to show a real preview we need to fetch + unzip client-side.
//
// Used by:
//   - My Creatives grid (gallery/page.tsx) — replaces the stack-icon
//     placeholder we used to render for .zip posts.
//   - Anywhere else that wants a real carousel preview (Schedule modal
//     post picker, Calendar tile, etc. — easy follow-up).
//
// Performance notes:
//   - Fetch + extract happens lazily once on mount per instance.
//   - 4 blob URLs are created and revoked on unmount to avoid leaking
//     memory as the user scrolls through a long gallery.
//   - For a typical 4×500KB PNG zip (~2MB), full extract is ~50-150ms
//     on a modern laptop. The grid still feels snappy since each card
//     extracts independently.

import { useEffect, useState } from 'react';

interface CarouselThumbnailProps {
  /** Signed URL to the zip in the gallery bucket. */
  zipUrl: string;
  /** Alt label for the whole collage (single value applied to all 4
   *  imgs — screen readers announce the post once). */
  alt?: string;
  /** Extra classes for the outer wrapper (e.g. rounding/positioning). */
  className?: string;
}

export default function CarouselThumbnail({
  zipUrl,
  alt = 'Carousel post',
  className = '',
}: CarouselThumbnailProps) {
  const [frames, setFrames] = useState<string[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    const createdUrls: string[] = [];

    (async () => {
      try {
        const res = await fetch(zipUrl);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const buf = new Uint8Array(await res.arrayBuffer());

        // Dynamic import keeps fflate out of the initial bundle for
        // pages that never show carousels.
        const { unzipSync } = await import('fflate');
        const entries = unzipSync(buf);

        // Pull out PNG frames in lexical order (the save flow names them
        // sequentially — `frame1.png`, `frame2.png`, etc.).
        const pngs = Object.entries(entries)
          .filter(([name]) => name.toLowerCase().endsWith('.png'))
          .sort(([a], [b]) => a.localeCompare(b))
          .slice(0, 4);

        if (pngs.length === 0) throw new Error('No PNG frames in zip');

        const urls: string[] = [];
        for (const [, bytes] of pngs) {
          const blob = new Blob([bytes], { type: 'image/png' });
          const url = URL.createObjectURL(blob);
          urls.push(url);
          createdUrls.push(url);
        }

        if (cancelled) return;
        setFrames(urls);
        setState('ready');
      } catch (err) {
        console.error('[CarouselThumbnail] extract failed', err);
        if (!cancelled) setState('error');
      }
    })();

    return () => {
      cancelled = true;
      // Revoke ALL blob URLs we created — even if some were never set
      // on state (e.g. unmount mid-extract).
      for (const url of createdUrls) {
        URL.revokeObjectURL(url);
      }
    };
  }, [zipUrl]);

  if (state === 'loading') {
    return <div className={`bg-surface-dark/15 animate-pulse ${className}`} />;
  }

  if (state === 'error' || frames.length === 0) {
    return (
      <div
        className={`bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center ${className}`}
      >
        <i className="ri-stack-line text-3xl text-dark-muted/40" />
      </div>
    );
  }

  // 2×2 collage. gap-px keeps a hairline divider between frames so the
  // viewer can tell they're 4 distinct images. Each frame is object-cover
  // so the grid stays uniform regardless of frame aspect.
  return (
    <div
      className={`grid grid-cols-2 grid-rows-2 gap-px bg-white ${className}`}
      role="img"
      aria-label={alt}
    >
      {frames.map((url, i) => (
        <img
          key={i}
          src={url}
          alt=""
          className="w-full h-full object-cover"
          // No loading="lazy" (Jul 14, Gotcha #277): inside the gallery's
          // opacity:0 → 1 framer-motion section reveal, Chrome's lazy observer
          // doesn't fire → the frame stays blank until a hover repaint. Frames
          // are already-fetched blob URLs, so eager is free.
          decoding="async"
        />
      ))}
      {/* Pad to 4 slots if the zip had fewer than 4 frames (defensive —
          should never happen with our save flow, but harmless if it does). */}
      {Array.from({ length: Math.max(0, 4 - frames.length) }).map((_, i) => (
        <div key={`pad-${i}`} className="bg-surface-dark/15" />
      ))}
    </div>
  );
}
